import { useState } from "react";
import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Bell,
  CalendarClock,
  Inbox,
  ListTodo,
  Settings,
  Menu,
  X,
  LogOut,
  ChevronRight,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/notifications", label: "Notifications", icon: Bell },
  { to: "/admin/appointments", label: "Appointments", icon: CalendarClock },
  { to: "/admin/contact", label: "Contact Requests", icon: Inbox },
  { to: "/admin/reminders", label: "Reminders", icon: ListTodo },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

function pageTitle(pathname) {
  const found = NAV.find((n) => (n.end ? pathname === n.to : pathname.startsWith(n.to)));
  return found?.label || "Admin";
}

export default function AdminLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const doLogout = async () => {
    await logout();
    toast.success("Signed out.");
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex" data-testid="admin-layout" style={{ background: "#FAF6EC" }}>
      {/* Sidebar (desktop) */}
      <aside
        className="hidden md:flex md:flex-col w-64 shrink-0"
        style={{ background: "#1B4332", color: "#FAF6EC" }}
        data-testid="admin-sidebar"
      >
        <SidebarInner onNavigate={() => {}} onLogout={doLogout} user={user} />
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="admin-mobile-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} />
          <aside
            className="absolute left-0 top-0 h-full w-72 flex flex-col"
            style={{ background: "#1B4332", color: "#FAF6EC" }}
          >
            <SidebarInner
              onNavigate={() => setDrawerOpen(false)}
              onLogout={doLogout}
              user={user}
            />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top navbar */}
        <header
          className="sticky top-0 z-30 border-b"
          style={{
            background: "rgba(250,246,236,0.9)",
            borderColor: "rgba(27,67,50,0.1)",
            backdropFilter: "blur(8px)",
          }}
          data-testid="admin-topbar"
        >
          <div className="flex items-center justify-between px-5 md:px-8 py-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-full border"
                style={{ borderColor: "rgba(27,67,50,0.2)", color: "#1B4332" }}
                aria-label="Open menu"
                data-testid="admin-drawer-open"
              >
                <Menu size={16} />
              </button>
              <nav className="flex items-center gap-1 text-sm" aria-label="Breadcrumb" data-testid="admin-breadcrumb">
                <Link to="/admin" className="text-[#1B4332]/70 hover:text-[#1B4332]">Admin</Link>
                {pathname !== "/admin" && (
                  <>
                    <ChevronRight size={14} className="text-[#1B4332]/50" />
                    <span className="text-[#1B4332] font-medium">{pageTitle(pathname)}</span>
                  </>
                )}
              </nav>
            </div>

            <UserMenu user={user} onLogout={doLogout} />
          </div>
          <div className="px-5 md:px-8 pb-5">
            <h1
              className="font-serif leading-none"
              style={{ color: "#1B4332", fontSize: "1.75rem" }}
              data-testid="admin-page-title"
            >
              {pageTitle(pathname)}
            </h1>
          </div>
        </header>

        <main className="flex-1 px-5 md:px-8 py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function SidebarInner({ onNavigate, onLogout, user }) {
  return (
    <>
      <div className="p-6 flex items-center justify-between">
        <Link to="/admin" onClick={onNavigate} className="flex items-center gap-3" data-testid="admin-sidebar-brand">
          <span
            className="inline-flex h-10 w-10 items-center justify-center rounded-full"
            style={{ background: "#C9A84C", color: "#1B4332" }}
          >
            <span className="font-serif text-lg leading-none">LR</span>
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-serif text-lg text-[#FAF6EC]">The Life Refinery</span>
            <span className="font-script text-xl -mt-1" style={{ color: "#C9A84C" }}>
              Admin
            </span>
          </span>
        </Link>
        <button
          type="button"
          onClick={onNavigate}
          className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-full"
          style={{ color: "#FAF6EC" }}
          aria-label="Close menu"
        >
          <X size={16} />
        </button>
      </div>

      <nav className="flex-1 px-3 space-y-1 mt-4" aria-label="Admin">
        {NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm transition-colors ${
                  isActive
                    ? "bg-[#C9A84C] text-[#1B4332] font-medium"
                    : "text-[#FAF6EC]/80 hover:bg-white/5"
                }`
              }
            >
              <Icon size={16} />
              {item.label}
            </NavLink>
          );
        })}
      </nav>

      <div className="p-4 border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
        <div className="flex items-center gap-3">
          <Avatar user={user} size={36} />
          <div className="min-w-0 flex-1">
            <div className="text-sm truncate text-[#FAF6EC]">{user?.name || "Admin"}</div>
            <div className="text-[11px] tracking-wider uppercase text-[#FAF6EC]/50 truncate">
              {(user?.role || "super_admin").replace("_", " ")}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onLogout}
          data-testid="admin-sidebar-logout"
          className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full py-2 text-sm border"
          style={{ borderColor: "rgba(201,168,76,0.5)", color: "#FAF6EC" }}
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </>
  );
}

function Avatar({ user, size = 32 }) {
  const initials = (user?.name || user?.email || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  if (user?.picture) {
    return (
      <img
        src={user.picture}
        alt=""
        className="rounded-full object-cover"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <span
      className="inline-flex items-center justify-center rounded-full font-medium"
      style={{
        background: "#C9A84C",
        color: "#1B4332",
        width: size,
        height: size,
        fontSize: size * 0.4,
      }}
    >
      {initials}
    </span>
  );
}

function UserMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-full px-2 py-1 hover:bg-black/5"
        data-testid="admin-user-menu-toggle"
      >
        <Avatar user={user} size={30} />
        <div className="hidden sm:flex flex-col items-start leading-tight">
          <span className="text-sm" style={{ color: "#1B4332" }}>{user?.name || "Admin"}</span>
          <span className="text-[10px] uppercase tracking-wider" style={{ color: "#6E6759" }}>
            {user?.email}
          </span>
        </div>
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-56 rounded-xl border overflow-hidden"
          style={{ background: "#fff", borderColor: "rgba(27,67,50,0.15)", boxShadow: "0 10px 30px -10px rgba(27,67,50,0.3)" }}
          onMouseLeave={() => setOpen(false)}
          data-testid="admin-user-menu"
        >
          <div className="px-4 py-3 border-b" style={{ borderColor: "rgba(27,67,50,0.08)" }}>
            <div className="text-sm font-medium" style={{ color: "#1B4332" }}>{user?.name}</div>
            <div className="text-xs truncate" style={{ color: "#6E6759" }}>{user?.email}</div>
          </div>
          <button
            type="button"
            onClick={onLogout}
            className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#FAF6EC] flex items-center gap-2"
            style={{ color: "#1B4332" }}
            data-testid="admin-user-menu-logout"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}
